import React from "react";
import { Phone, Mail, MapPin, Clock, MessageCircle } from "lucide-react";
import SEO from "@/components/SEO";
import { PageHeader } from "./ServicesPage";
import ContactForm from "@/components/ContactForm";
import { BUSINESS, MEDIA } from "@/constants/business";

export default function ContactPage() {
  return (
    <>
      <SEO
        title="Contact Vila Elevators & Escalators | Bangalore | +91 90712 21100"
        description="Contact Vila Elevators & Escalators — Bommasandra Industrial Area, Bengaluru. Call +91 90712 21100 or email info@vilaelevators.in for a quote."
        path="/contact"
        keywords="contact vila elevators, elevator company phone bangalore, lift dealer bommasandra"
      />

      <PageHeader
        eyebrow="Contact"
        title="Let's elevate your project."
        desc="Speak to our team for a site survey, custom quote, or AMC enquiry — we respond within one business day."
        image={MEDIA.hero}
      />

      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-1 md:grid-cols-4 gap-6">
          <InfoCard icon={<Phone />} label="Call" value={BUSINESS.phone} href={`tel:${BUSINESS.phoneRaw}`} testid="contact-info-phone" />
          <InfoCard icon={<Mail />} label="Email" value={BUSINESS.email} href={`mailto:${BUSINESS.email}`} testid="contact-info-email" />
          <InfoCard icon={<MessageCircle />} label="WhatsApp" value="Chat with us" href={`https://wa.me/${BUSINESS.whatsapp}`} testid="contact-info-whatsapp" />
          <InfoCard icon={<Clock />} label="Hours" value={BUSINESS.hours} testid="contact-info-hours" />
        </div>
      </section>

      <section className="py-16 bg-[#faf7f2]/40 border-y border-[#e5ddd0]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-16">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#b08d57] mb-4">Visit Our Factory</div>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-[#1a1a1a] mb-6">
              Bommasandra Industrial Area, Bengaluru.
            </h2>
            <div className="flex gap-4 text-[#4a4a4a] mb-6">
              <MapPin className="w-5 h-5 text-[#b08d57] flex-shrink-0 mt-1" />
              <div className="leading-relaxed">
                {BUSINESS.address.line1}<br />
                {BUSINESS.address.line2}<br />
                {BUSINESS.address.city} – {BUSINESS.address.postal}<br />
                {BUSINESS.address.state}, {BUSINESS.address.country}
              </div>
            </div>
            <a
              href={BUSINESS.mapLink}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-directions-link"
              className="inline-flex items-center gap-2 text-[#b08d57] hover:text-[#b08d57] font-semibold uppercase text-xs tracking-wider mb-6"
            >
              Get Directions →
            </a>
            <div className="border border-[#e5ddd0] aspect-video overflow-hidden mt-4" data-testid="contact-map-embed">
              <iframe
                title="Vila Elevators location"
                src={BUSINESS.mapEmbedNoKey}
                loading="lazy"
                className="w-full h-full grayscale-[30%]"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>

          <div className="border border-[#e5ddd0] bg-[#faf7f2] p-8 lg:p-10">
            <h3 className="font-display text-2xl font-bold text-[#1a1a1a] mb-6">Send an enquiry</h3>
            <ContactForm sourcePage="contact" />
          </div>
        </div>
      </section>
    </>
  );
}

function InfoCard({ icon, label, value, href, testid }) {
  const inner = (
    <div className="border border-[#e5ddd0] bg-[#faf7f2]/40 p-6 h-full hover:border-[#b08d57]/50 transition-colors">
      <div className="w-10 h-10 border border-[#b08d57]/40 text-[#b08d57] flex items-center justify-center mb-4">
        {icon}
      </div>
      <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#8a8a8a] mb-1">{label}</div>
      <div className="text-[#1a1a1a] font-display text-lg">{value}</div>
    </div>
  );
  return href ? (
    <a href={href} target={href.startsWith("http") ? "_blank" : undefined} rel="noopener noreferrer" data-testid={testid} className="block">
      {inner}
    </a>
  ) : (
    <div data-testid={testid}>{inner}</div>
  );
}
